import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { schemaService } from '../../services/schemaService';
import { categoryService } from '../../services/categoryService';

const SchemaList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [schemas, setSchemas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [baseSchemaFilter, setBaseSchemaFilter] = useState('all');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [totalSchemas, setTotalSchemas] = useState(0);

  // Calculate total pages
  const totalPages = Math.ceil(totalSchemas / rowsPerPage) || 1;

  // Load categories
  useEffect(() => {
    loadCategories();
  }, []);

  // Load schemas when filters or pagination change
  useEffect(() => {
    loadSchemas();
  }, [selectedCategory, activeFilter, baseSchemaFilter, page, rowsPerPage]);

  const loadCategories = async () => {
    try {
      const response = await categoryService.getCategoryTree();
      if (response.success) {
        const categoriesData = response.data?.data || response.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadSchemas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query params
      let params = {
        page: page + 1, // Backend expects 1-based pagination
        page_size: rowsPerPage,
      };
      
      if (selectedCategory) {
        params.category__sid = selectedCategory;
      }
      if (activeFilter !== 'all') {
        params.is_active = activeFilter === 'active';
      }
      if (baseSchemaFilter !== 'all') {
        params.is_base_schema = baseSchemaFilter === 'base';
      }

      const response = await schemaService.getSchemas(params);
      if (response.success) {
        const schemasData = response.data.results || response.data || [];
        setSchemas(Array.isArray(schemasData) ? schemasData : []);
        setTotalSchemas(response.data.count || schemasData.length);
      } else {
        setError('Failed to load schemas');
      }
    } catch (err) {
      setError('Error loading schemas: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedCategory, activeFilter, baseSchemaFilter]);

  const handleDelete = async (schemaId) => {
    if (!window.confirm('Are you sure you want to delete this schema?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await schemaService.deleteSchema(schemaId);
      if (response.success) {
        setSuccess('Schema deleted successfully');
        loadSchemas();
      } else {
        setError('Failed to delete schema');
      }
    } catch (err) {
      setError('Error deleting schema: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (schema) => {
    navigate(`/admin/schema-manager?schema=${schema.sid}`);
  };

  const handleView = (schema) => {
    navigate(`/admin/schema-manager?schema=${schema.sid}&mode=view`);
  };

  const handleCreate = () => {
    navigate('/admin/schema-manager');
  };

  const handleRefresh = () => {
    setPage(0);
    loadSchemas();
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  // Flatten category tree for dropdown
  const flattenCategories = (cats, level = 0) => {
    let result = [];
    cats.forEach(cat => {
      result.push({
        sid: cat.sid,
        name: '—'.repeat(level) + ' ' + cat.name,
        level
      });
      if (cat.children && cat.children.length > 0) {
        result = result.concat(flattenCategories(cat.children, level + 1));
      }
    });
    return result;
  };

  const flatCategories = flattenCategories(categories);

  // Add serial numbers to schemas for the current page
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    { field: 'sn', headerName: 'S/N', width: 80, sortable: false, filterable: false },
    {
      field: 'category_name',
      headerName: 'Category',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'version',
      headerName: 'Version',
      width: 100,
      renderCell: (params) => {
        if (!params || params.value === null || params.value === undefined) return null;
        return <Chip label={`v${params.value}`} size="small" variant="outlined" />;
      },
    },
    {
      field: 'is_base_schema',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => {
        if (!params) return null;
        return params.value ? (
          <Chip label="Base Schema" size="small" color="primary" />
        ) : (
          <Chip label="Category" size="small" />
        );
      },
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => {
        if (!params) return null;
        return (
          <Chip 
            label={params.value ? 'Active' : 'Inactive'} 
            size="small"
            color={params.value ? 'success' : 'default'}
          />
        );
      },
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      width: 150,
      valueGetter: (value, row) => {
        if (!row || !row.created_by) return 'N/A';
        return row.created_by.name || 'N/A';
      },
    },
    {
      field: 'updated_by',
      headerName: 'Updated By',
      width: 150,
      valueGetter: (value, row) => {
        if (!row || !row.updated_by) return 'N/A';
        return row.updated_by.name || 'N/A';
      },
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      width: 180,
      valueGetter: (value, row) => {
        if (!value) return 'N/A';
        try {
          return new Date(value).toLocaleString();
        } catch (error) {
          return 'Invalid Date';
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        return (
          <Box>
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() => handleView(params.row)}
                color="info"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleEdit(params.row)}
                color="primary"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDelete(params.row.sid)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  if (loading && schemas.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading schemas...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          Schema Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
            onClick={handleCreate}
          >
            Create New Schema
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filter Section */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box>
              <Typography variant="h6" sx={{ color: '#DC143C', fontWeight: 'bold' }}>
                <FilterListIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Filters
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Filter schemas by category, status, or type
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              Showing {schemas.length} of {totalSchemas} schemas
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Category"
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setPage(0);
                }}
                fullWidth
                size="small"
              >
                <MenuItem value="">All Categories</MenuItem>
                {flatCategories.map((cat) => (
                  <MenuItem key={cat.sid} value={cat.sid}>
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                label="Status"
                value={activeFilter}
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                  setPage(0);
                }}
                fullWidth
                size="small"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                label="Type"
                value={baseSchemaFilter}
                onChange={(e) => {
                  setBaseSchemaFilter(e.target.value);
                  setPage(0);
                }}
                fullWidth
                size="small"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="base">Base Schema</MenuItem>
                <MenuItem value="category">Category Schema</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {schemas.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', py: 8 }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  No schemas found
                </Typography>
                <Button variant="outlined" onClick={handleRefresh}>
                  Refresh Data
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(schemas, page, rowsPerPage)}
                columns={columns}
                getRowId={(row) => row.sid}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1200,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: theme => theme.palette.primary.main,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Rows per page:
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SchemaList;
